/**
 * Real API Service Layer for Meridian Platform
 * 
 * Connects the frontend to Azure Function App endpoints.
 * Falls back to empty data when APIs are unavailable.
 */

import { PendingAction, AuditEntry, StatsSummary } from '../types';
import { API_BASE, apiFetch } from './base';

// Function App base URL — configurable via env

// ============ Types matching backend ============

export interface RcmDashboardResponse {
  agent: {
    id: string;
    name: string;
    workstream: string;
    version: string;
    status: string;
    portcoId: string;
  };
  kpis: { id: string; label: string; value: string; trend: string; trendLabel: string }[];
  metrics: {
    totalClaims: number;
    deniedClaims: number;
    pendingAppeals: number;
    totalDenialAmount: number;
    recoveredAmount: number;
    avgProcessingDays: number;
    agentRunsCount: number;
    successRate: number;
  };
  recentRuns: {
    id: string;
    agentName: string;
    summary: string;
    status: string;
    startTime: string;
    endTime: string;
    claimsAnalyzed: number;
    denialsFound: number;
    appealsGenerated: number;
    avgConfidence: number;
  }[];
}

export interface RcmClaimsResponse {
  claims: {
    id: string;
    portcoId: string;
    portcoName: string;
    patientName: string;
    claimId: string;
    cptCode: string;
    claimAmount: number;
    denialDate: string;
    denialReason: string;
    denialCode: string;
    status: string;
    payerName: string;
    appealDeadline: string;
  }[];
  count: number;
  totalAmount: number;
}

export interface RcmApproval {
  id: string;
  portcoId: string;
  portcoName: string;
  agentId: string;
  agentName: string;
  workstream: string;
  actionType: string;
  actionDescription: string;
  status: string;
  priority: string;
  impact: string;
  createdAt: string;
  aiRecommendation: string;
  confidence: number;
  actionPayload: any;
}

export interface RcmApprovalsResponse {
  approvals: RcmApproval[];
  count: number;
}

export interface AgentConfig {
  portcoId: string;
  workstream: string;
  agentId: string;
  name: string;
  enabled: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AgentRunResponse {
  runId?: string;
  workstream?: string;
  portcoId?: string;
  mode?: 'queued' | 'inline';
  message?: string;
  itemsAnalyzed?: number;
  recommendationsGenerated?: number;
  tokensUsed?: number;
  modelUsed?: string;
  estimatedCost?: number;
  error?: string;
}

export interface RcmTriggerResponse {
  runId: string;
  status: string;
  message: string;
  claimsQueued: number;
}

// ============ API Helpers ============

async function apiGet<T>(path: string): Promise<T | null> {
  try {
    const res = await apiFetch(`${API_BASE}${path}`);
    if (!res.ok) {
      console.warn(`[API] GET ${path} failed: ${res.status}`);
      return null;
    }
    return await res.json() as T;
  } catch (err) {
    console.warn(`[API] GET ${path} error:`, err);
    return null;
  }
}

async function apiPost<T>(path: string, body?: any): Promise<T | null> {
  try {
    const res = await apiFetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      console.warn(`[API] POST ${path} failed: ${res.status}`);
      return null;
    }
    return await res.json() as T;
  } catch (err) {
    console.warn(`[API] POST ${path} error:`, err);
    return null;
  }
}

async function apiPostRaw<T>(path: string, body?: any): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data as T;
}

// ============ RCM API Methods ============

export const rcmApi = {
  async getDashboard(portcoId: string = 'all'): Promise<RcmDashboardResponse | null> {
    return apiGet<RcmDashboardResponse>(`/rcm/dashboard?portcoId=${encodeURIComponent(portcoId)}`);
  },

  /**
   * Fetch a printable appeal packet (single claim, or bundle=all for every
   * in-flight appeal). Returns raw HTML — callers turn it into a Blob
   * download so Entra bearer auth works (cookies alone can't navigate).
   */
  async fetchAppealPacket(portcoId: string, claimId?: string, bundle = false): Promise<string | null> {
    const qs = new URLSearchParams({ portcoId });
    if (claimId) qs.set('claimId', claimId);
    if (bundle) qs.set('bundle', 'all');
    try {
      const res = await apiFetch(`${API_BASE}/rcm/appeal-packet?${qs.toString()}`);
      if (!res.ok) return null;
      return await res.text();
    } catch {
      return null;
    }
  },

  async getClaims(portcoId: string = 'all'): Promise<RcmClaimsResponse | null> {
    return apiGet<RcmClaimsResponse>(`/rcm/claims?portcoId=${encodeURIComponent(portcoId)}`);
  },

  async getApprovals(portcoId: string = 'all'): Promise<RcmApprovalsResponse | null> {
    return apiGet<RcmApprovalsResponse>(`/rcm/approvals?portcoId=${encodeURIComponent(portcoId)}&status=pending`);
  },

  async triggerAgentRun(portcoId: string, workstream: string = 'rcm'): Promise<RcmTriggerResponse | null> {
    return apiPost<RcmTriggerResponse>(`/rcm/trigger`, { portcoId, workstream });
  },

  // ── Per-tenant agent on/off switches ────────────────────────────────────
  async getAgentConfigs(portcoId: string = 'all'): Promise<AgentConfig[]> {
    const res = await apiGet<{ agents: AgentConfig[] }>(`/agents/config?portcoId=${encodeURIComponent(portcoId)}`);
    return res?.agents ?? [];
  },

  async setAgentConfig(portcoId: string, workstream: string, enabled: boolean): Promise<AgentConfig> {
    const res = await apiPostRaw<{ agent: AgentConfig }>(`/agents/config`, { portcoId, workstream, enabled });
    return res.agent;
  },

  // ── Generic agent run trigger ───────────────────────────────────────────
  async runAgent(workstream: string, portcoId: string): Promise<AgentRunResponse> {
    return await apiPostRaw<AgentRunResponse>(`/agents/${encodeURIComponent(workstream)}/run`, { portcoId });
  },

  async approveAction(checkpointId: string, portcoId: string, approvedBy: string = 'Sarah Chen'): Promise<any> {
    return apiPost(`/rcm/approve`, { checkpointId, portcoId, approvedBy });
  },

  async rejectAction(checkpointId: string, portcoId: string, rejectedBy: string = 'Sarah Chen', reason?: string): Promise<any> {
    return apiPost(`/rcm/reject`, { checkpointId, portcoId, rejectedBy, rejectionReason: reason });
  },

  async seedData(): Promise<any> {
    return apiPost(`/rcm/seed`);
  },
};
